import { useEffect, useState } from 'react';
import type { AiQualificationSettings } from '@orion/contracts';
import { PageHeader } from '../../../app/shell.js';
import { useSettingsQuery, useUpdateSettings } from '../settings/api.js';

/** Admin ▸ Integrations ▸ AI lead qualification (docs §6). Same
 * draft-and-autosave pattern as `LeadConfigPage`'s `DuplicatesCard` — every
 * field writes through to the `ai_qualification` settings namespace
 * immediately, no separate Save button. There's no API key or model field
 * here on purpose: classification goes through a free-then-paid provider
 * chain (Gemini → OpenAI → Anthropic), each with its own env var/Doppler
 * secret (`apps/api/src/config/env.ts`) set once by whoever provisions the
 * workspace — this page never receives or displays any of them, the
 * spec's "never expose API keys in frontend code" requirement enforced by
 * simply not wiring a field for it at all. */
export function AiQualificationSettingsPage() {
  const { data, isLoading } = useSettingsQuery<AiQualificationSettings>('ai_qualification');
  const update = useUpdateSettings<AiQualificationSettings>('ai_qualification');
  const [draft, setDraft] = useState<AiQualificationSettings | null>(null);

  useEffect(() => {
    if (data && draft === null) setDraft(data);
  }, [data, draft]);

  const save = (patch: Partial<AiQualificationSettings>) => {
    if (!draft) return;
    const next = { ...draft, ...patch };
    setDraft(next);
    update.mutate(next);
  };

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Admin' }, { label: 'AI lead qualification' }]} />
      <main className="flex flex-1 flex-col gap-4 overflow-y-auto px-7 py-5">
        {!isLoading && draft && (
          <section aria-labelledby="ai-qual-h" className="flex max-w-xl flex-col gap-4 rounded-dialog border border-border bg-surface p-4.5 shadow-card">
            <div className="flex items-center justify-between">
              <h2 id="ai-qual-h" className="text-[15px] font-semibold">
                AI lead qualification
              </h2>
              <label className="flex items-center gap-2 text-[13px] font-semibold">
                Enabled
                <input type="checkbox" checked={draft.enabled} onChange={(e) => save({ enabled: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
              </label>
            </div>
            <p className="text-xs text-text-muted">
              When enabled, every lead-creation path (Pulse email replies, WhatsApp, MCP/API-key intake, public web forms, and the manual
              "New Lead" form) is classified before becoming a Lead — newsletters, spam, and other non-sales messages stop reaching the
              pipeline automatically. Disabled (the default), behavior is unchanged from before this feature existed.
            </p>
            <p className="text-xs text-text-muted">
              Classification tries, in order, a free provider then two paid fallbacks — so one provider running out of quota doesn't
              block lead creation tenant-wide. Each needs its own key set via Doppler/environment configuration to be tried at all:
            </p>
            <ol className="flex flex-col gap-1 text-xs text-text-muted">
              <li>
                <code className="rounded-xs bg-surface-subtle px-1 font-mono text-[11px]">GEMINI_API_KEY</code> — Google's free tier.
                Google may use free-tier requests to improve their models; avoid this as your only configured provider if that matters
                for your data.
              </li>
              <li>
                <code className="rounded-xs bg-surface-subtle px-1 font-mono text-[11px]">OPENAI_API_KEY</code> — this feature's
                original provider.
              </li>
              <li>
                <code className="rounded-xs bg-surface-subtle px-1 font-mono text-[11px]">ANTHROPIC_API_KEY</code> — Claude.
              </li>
            </ol>
            <p className="text-xs text-text-muted">
              Each also has an optional <code className="rounded-xs bg-surface-subtle px-1 font-mono text-[11px]">*_MODEL</code> override
              (e.g. <code className="rounded-xs bg-surface-subtle px-1 font-mono text-[11px]">GEMINI_MODEL</code>) — leave unset to use a
              sensible default.
            </p>

            <div className="border-t border-divider" />

            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
              Default priority for an auto-created lead
              <select
                value={draft.defaultPriority}
                onChange={(e) => save({ defaultPriority: e.target.value as AiQualificationSettings['defaultPriority'] })}
                className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal"
              >
                <option value="hot">Hot</option>
                <option value="warm">Warm</option>
                <option value="cold">Cold</option>
                <option value="none">None</option>
              </select>
            </label>

            <div className="border-t border-divider" />

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Confidence thresholds</legend>
              <label className="flex items-center justify-between gap-3 text-[13px]">
                <span>
                  <span className="font-semibold">Auto-create a lead</span>
                  <span className="text-text-muted"> — confidence at or above this creates the lead automatically</span>
                </span>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={draft.autoCreateThreshold}
                  onChange={(e) => save({ autoCreateThreshold: Number(e.target.value) })}
                  className="w-16 shrink-0 rounded-xs border border-border-strong px-1.5 py-1 font-mono text-xs"
                />
              </label>
              <label className="flex items-center justify-between gap-3 text-[13px]">
                <span>
                  <span className="font-semibold">Queue for review</span>
                  <span className="text-text-muted"> — confidence at or above this (but below auto-create) goes to AI Lead Review</span>
                </span>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={draft.reviewThreshold}
                  onChange={(e) => save({ reviewThreshold: Number(e.target.value) })}
                  className="w-16 shrink-0 rounded-xs border border-border-strong px-1.5 py-1 font-mono text-xs"
                />
              </label>
              <p className="text-xs text-text-muted">Below the review threshold, the email is recorded but no lead is created or queued.</p>
            </fieldset>

            <div className="border-t border-divider" />

            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={draft.enableAutoClassification} onChange={(e) => save({ enableAutoClassification: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
              <span className="font-semibold">Automatic classification</span>
              <span className="text-text-muted">— apply the AI's decision without review when confidence allows it</span>
            </label>
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={draft.enableSummary} onChange={(e) => save({ enableSummary: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
              <span className="font-semibold">AI summary</span>
              <span className="text-text-muted">— show the AI's one-sentence reasoning on the AI Lead Review screen</span>
            </label>
          </section>
        )}
      </main>
    </>
  );
}
